import Image from "next/legacy/image";
import React from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { imageBuilder } from "../lib/sanity";
import { blurDataURL } from "../lib/constants";
import BlockContent from "../components/block-content";
import { Testimonials } from "./testimonials";

const ImageFeature = ({ data, index, reverse }) => {
  let mediaLeft = index % 2;
  if (reverse) mediaLeft = !mediaLeft;

  const imageContainerClassName = mediaLeft ? "lg:col-start-1" : "";
  const imageWrapperClassName = mediaLeft
    ? "pr-4 -ml-48 sm:pr-6 lg:-ml-72 lg:px-0"
    : "pl-4 -mr-48 sm:pl-6 lg:-mr-72 lg:px-0";
  const hasTestimonial = data.testimonial?.quote;

  return (
    <div className="relative">
      <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
        <div
          className={`px-4 max-w-xl mx-auto sm:px-6 ${
            hasTestimonial ? "lg:py-16" : "lg:py-32"
          } lg:max-w-none lg:mx-0 lg:px-0`}
        >
          <div>
            <div>
              <span className="h-12 w-12 rounded-md flex items-center justify-center bg-accent-600">
                <SparklesIcon
                  className="h-6 w-6 text-white"
                  aria-hidden="true"
                />
              </span>
            </div>
            <div className="mt-6">
              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
                {data.title}
              </h2>

              {data.description ? (
                <p className="mt-4 text-lg text-gray-500 whitespace-pre-wrap">
                  {data.description}
                </p>
              ) : null}

              {data.descriptionRichText && data.descriptionRichText?.length ? (
                <BlockContent
                  className="mt-4 prose-lg prose text-gray-500 block-content"
                  content={data.descriptionRichText}
                />
              ) : null}

              {data.button && data.button.text ? (
                <div className="mt-6">
                  <a
                    href={data.button.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-accent-600 hover:bg-accent-700"
                  >
                    {data.button.text}
                  </a>
                </div>
              ) : null}
            </div>
          </div>

          {data.testimonials?.length ? (
            <Testimonials testimonials={data.testimonials} className="mt-8" />
          ) : null}

          {data.testimonial && data.testimonial.quote ? (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <blockquote>
                <div>
                  <p className="text-base text-gray-500">
                    &ldquo;{data.testimonial.quote}&rdquo;
                  </p>
                </div>
                <footer className="mt-3">
                  <div className="flex items-center space-x-3">
                    {data.testimonial.author?.image ? (
                      <div className="flex-shrink-0">
                        <div className="h-6 w-6 rounded-full relative">
                          <Image
                            draggable={false}
                            alt=""
                            layout="fill"
                            className="rounded-full"
                            placeholder="blur"
                            blurDataURL={blurDataURL}
                            src={imageBuilder(data.testimonial.author?.image)
                              .width(24)
                              .height(24)
                              .dpr(2)
                              .crop("entropy")
                              .url()}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className="text-base font-medium text-gray-700">
                      {data.testimonial.author?.name}
                    </div>
                  </div>
                </footer>
              </blockquote>
            </div>
          ) : null}
        </div>

        <div
          className={`mt-12 sm:mt-16 lg:mt-0 ${imageContainerClassName} ${imageWrapperClassName}`}
        >
          <div className="relative aspect-w-5 aspect-h-3 shadow-xl lg:pb-0 lg:w-full lg:h-full">
            <Image
              draggable={false}
              alt=""
              layout="fill"
              className="rounded-xl shadow-xl ring-1 ring-black ring-opacity-5"
              placeholder="blur"
              objectFit="cover"
              blurDataURL={blurDataURL}
              src={imageBuilder(data.image)
                .width(841)
                .height(505)
                .dpr(2)
                .crop("entropy")
                .url()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageFeature;
